/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * Change control (/cutovers): every cut-over request of the project, the approvals queue and
 * the calendar in one Ops table. A row opens the request in a drawer (/cutovers/<id>) with its
 * timeline and the Approve / Reject / Cancel actions.
 */

import { observer } from "mobx-react";
import * as React from "react";
import styled, { css } from "styled-components";
import { useNavigate, useParams } from "react-router";
import { Check, ClipboardCheck, LoaderCircle, SearchX, UserCheck, X } from "lucide-react";

import CutoverDetails from "@src/components/modules/CutoverModule/CutoverDetails";
import DrApprovals from "@src/components/modules/DrModule/DrApprovals";
import {
  ACTIVE_STATUSES,
  formatDuration,
  formatWhen,
  formatWindow,
  instancesLabel,
  relative,
  requesterName,
  statusInfo,
  waitingForMe,
} from "@src/components/modules/CutoverModule/cutoverUtils";
import Navigation from "@src/components/modules/NavigationModule/Navigation";
import MainTemplate from "@src/components/modules/TemplateModule/MainTemplate";
import { onSwiftRefresh } from "@src/components/modules/TemplateModule/TopBar/refresh";
import PageHeader from "@src/components/smart/PageHeader";
import EmptyState from "@src/components/ui/Lists/EmptyState";
import ListToolbar, {
  ClearFiltersButton,
  FilterSelect,
  RefreshButton,
  Segmented,
  ToolbarCount,
  ToolbarDateRange,
  ToolbarSearch,
  ToolbarSpacer,
  matchesSearch,
  useListQuery,
} from "@src/components/ui/Lists/ListToolbar";
import type { ListQuery } from "@src/components/ui/Lists/ListToolbar";
import {
  Cell,
  ListHeader,
  ListRow,
  Muted,
  Pill,
  Primary,
  Secondary,
  Stack,
  StatePill,
  T9,
  formatShortDateTime,
  parseApiDate,
  pluralise,
  timeAgo,
} from "@src/components/ui/Lists/ListTable";
import type { ListColumn } from "@src/components/ui/Lists/ListTable";
import { Notice } from "@src/components/ui/OpsDialog";
import cutoverSource, { apiErrorMessage } from "@src/sources/CutoverSource";
import deploymentSource from "@src/sources/DeploymentSource";
import transferSource from "@src/sources/TransferSource";
import userStore from "@src/stores/UserStore";

import type { CutoverRequest } from "@src/@types/Cutover";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";

export const CUTOVER_COLUMNS: ListColumn[] = [
  { key: "transfer", label: "Transfer", width: "minmax(200px, 2fr)" },
  { key: "window", label: "Change window", width: "minmax(180px, 1.5fr)" },
  { key: "status", label: "Status", width: "160px" },
  { key: "requested", label: "Requested by", width: "120px" },
  { key: "approvals", label: "Approvals", width: "110px" },
  { key: "deployment", label: "Deployment", width: "110px" },
  { key: "created", label: "Created", width: "104px" },
];

export const STATUS_GROUPS: { [group: string]: string[] } = {
  pending: ["pending_approval"],
  scheduled: ["approved", "scheduled"],
  running: ["started"],
  completed: ["completed"],
  closed: ["failed", "rejected", "cancelled", "expired"],
};
const FILTER_KEYS = ["q", "status", "transfer", "from", "to", "mine"];
const POLL_MS = 15000;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
`;
const Card = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  background: var(--t9-bg-surface);
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
  overflow: hidden;
`;
const Scroll = styled.div`
  flex-grow: 1;
  min-height: 0;
  overflow: auto;
`;
const Inner = styled.div`
  min-width: 1080px;
`;
const Footnote = styled.div`
  padding: 10px 16px;
  border-top: 1px solid ${T9.slate200};
  font-size: 12px;
  color: ${T9.slate500};
`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 48px;
  color: ${T9.slate500};
  font-size: 14px;
  .spin {
    animation: cut-spin 1s linear infinite;
  }
  @keyframes cut-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const ToggleButton = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  box-sizing: border-box;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid ${T9.slate300};
  background: var(--t9-bg-surface);
  font: inherit;
  font-size: 13px;
  color: ${T9.slate700};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover {
    border-color: ${T9.slate400};
  }
  ${props =>
    props.active
      ? css`
          border-color: ${T9.brand300};
          background: ${T9.brand50};
          color: ${T9.brand700};
          font-weight: 600;
        `
      : ""}
  .count {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    box-sizing: border-box;
    border-radius: 9999px;
    background: ${props => (props.active ? T9.brand600 : "var(--t9-warning-50)")};
    color: ${props => (props.active ? "white" : "var(--t9-warning-700)")};
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
    text-align: center;
  }
`;
const Decisions = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: 12px;
  line-height: 16px;
  .d {
    display: flex;
    align-items: center;
    gap: 4px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;
const DeploymentLink = styled.a`
  color: ${T9.brand600};
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const pad2 = (n: number) => String(n).padStart(2, "0");
/** "22:30" on the same day, "Sat 01:00" on another. */
const endLabel = (start: Date, end: Date) =>
  start.toDateString() === end.toDateString()
    ? `${pad2(end.getHours())}:${pad2(end.getMinutes())}`
    : `${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][end.getDay()]} ${pad2(end.getHours())}:${pad2(end.getMinutes())}`;

/** Whether a request's window overlaps the [from, to] days (YYYY-MM-DD, local, both inclusive). */
export const windowOverlaps = (r: CutoverRequest, from: string, to: string) => {
  if (!from && !to) return true;
  const s = parseApiDate(r.window_start)?.getTime() ?? 0;
  const e = parseApiDate(r.window_end)?.getTime() ?? 0;
  if (from && e < new Date(`${from}T00:00:00`).getTime()) return false;
  if (to && s > new Date(`${to}T23:59:59.999`).getTime()) return false;
  return true;
};

type Lookup = { [id: string]: { name: string; instances: string[] } };

type Props = {
  query: ListQuery;
  selectedId: string | null;
  onNavigate: (path: string) => void;
};
type State = {
  requests: CutoverRequest[];
  transfers: Lookup;
  deployments: { [id: string]: string };
  loading: boolean;
  loaded: boolean;
  error: string | null;
  selected: CutoverRequest | null;
};

@observer
class CutoversPageContent extends React.Component<Props, State> {
  state: State = {
    requests: [],
    transfers: {},
    deployments: {},
    loading: false,
    loaded: false,
    error: null,
    selected: null,
  };

  offSwiftRefresh?: () => void;

  timer: any = null;

  unmounted = false;

  componentDidMount() {
    document.title = "Change control · SwiftIQ";
    this.offSwiftRefresh = onSwiftRefresh(() => this.load());
    this.load();
    this.timer = setInterval(() => this.load(true), POLL_MS);
  }

  componentDidUpdate(prev: Props) {
    if (prev.selectedId !== this.props.selectedId) this.syncSelected();
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.offSwiftRefresh?.();
    clearInterval(this.timer);
  }

  async load(quiet = false) {
    if (!quiet) this.setState({ loading: true });
    try {
      const [requests, transfers, deployments] = await Promise.all([
        cutoverSource.list({ limit: 1000 }, { skipLog: quiet }),
        transferSource.getTransfers({ skipLog: true, quietError: true }).catch(() => []),
        deploymentSource.getDeployments({ skipLog: true, quietError: true }).catch(() => []),
      ]);
      if (this.unmounted) return;
      const t: Lookup = {};
      transfers.forEach(x => {
        t[x.id] = { name: x.notes || x.instances?.[0] || x.id, instances: x.instances || [] };
      });
      const d: { [id: string]: string } = {};
      deployments.forEach(x => {
        d[x.id] = x.last_execution_status;
      });
      this.setState({ requests, transfers: t, deployments: d, error: null, loaded: true }, () => this.syncSelected());
    } catch (err: any) {
      if (!quiet) this.setState({ error: apiErrorMessage(err, "Could not load the cut-over requests.") });
    } finally {
      if (!this.unmounted && !quiet) this.setState({ loading: false });
    }
  }

  async syncSelected() {
    const id = this.props.selectedId;
    if (!id) {
      if (this.state.selected) this.setState({ selected: null });
      return;
    }
    const fromList = this.state.requests.find(r => r.id === id);
    if (fromList) {
      this.setState({ selected: fromList });
      return;
    }
    if (!this.state.loaded) return;
    try {
      const one = await cutoverSource.get(id, { skipLog: true });
      if (!this.unmounted) this.setState({ selected: one });
    } catch (err) {
      if (!this.unmounted) this.setState({ error: apiErrorMessage(err, "Could not load the cut-over request.") });
    }
  }

  get user() {
    return userStore.loggedUser;
  }

  get filtersActive() {
    return FILTER_KEYS.some(k => this.props.query.get(k));
  }

  clearFilters() {
    const changes: { [k: string]: null } = {};
    FILTER_KEYS.forEach(k => {
      changes[k] = null;
    });
    this.props.query.set(changes);
  }

  transferName(r: CutoverRequest) {
    return this.state.transfers[r.transfer_id]?.name || null;
  }

  filtered(except?: string): CutoverRequest[] {
    const q = this.props.query;
    const text = q.get("q");
    const status = q.get("status");
    const transfer = q.get("transfer");
    const mine = q.get("mine");
    return this.state.requests.filter(r => {
      if (except !== "status" && status && !(STATUS_GROUPS[status] || []).includes(r.status)) return false;
      if (transfer && r.transfer_id !== transfer) return false;
      if (except !== "mine" && mine && !waitingForMe(r, this.user)) return false;
      if (!windowOverlaps(r, q.get("from"), q.get("to"))) return false;
      if (text) {
        return matchesSearch(text, [
          this.transferName(r),
          r.reason,
          requesterName(r),
          r.id,
          r.transfer_id,
          r.deployment_id,
          ...(r.instances || []),
          ...(r.approvals || []).map(a => a.user_name),
        ]);
      }
      return true;
    });
  }

  replaceRequest(updated: CutoverRequest) {
    this.setState(prev => ({
      requests: prev.requests.some(r => r.id === updated.id)
        ? prev.requests.map(r => (r.id === updated.id ? updated : r))
        : [updated, ...prev.requests],
      selected: prev.selected && prev.selected.id === updated.id ? updated : prev.selected,
    }));
    this.load(true);
  }

  open(r: CutoverRequest) {
    this.props.onNavigate(`/cutovers/${r.id}${window.location.search}`);
  }

  rowActions(r: CutoverRequest): DropdownAction[] {
    return [
      { label: "Open", action: () => this.open(r) },
      {
        label: "Approve or reject…",
        hidden: !waitingForMe(r, this.user),
        action: () => this.open(r),
      },
      { label: "Open transfer", action: () => this.props.onNavigate(`/transfers/${r.transfer_id}`) },
      {
        label: "Open deployment",
        hidden: !r.deployment_id,
        action: () => this.props.onNavigate(`/deployments/${r.deployment_id}`),
      },
    ];
  }

  renderToolbar(records: CutoverRequest[]) {
    const q = this.props.query;
    const forStatus = this.filtered("status");
    const count = (g: string) => forStatus.filter(r => STATUS_GROUPS[g].includes(r.status)).length;
    const waiting = this.filtered("mine").filter(r => waitingForMe(r, this.user)).length;
    const transferOptions = Array.from(new Set(this.state.requests.map(r => r.transfer_id)))
      .map(id => ({ value: id, label: this.state.transfers[id]?.name || id.slice(0, 8) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return (
      <ListToolbar>
        <ToolbarSearch
          value={q.get("q")}
          placeholder="Search transfer, reason, person or ID"
          onChange={v => q.set({ q: v })}
          width={250}
        />
        <Segmented
          label="Status"
          value={q.get("status")}
          onChange={v => q.set({ status: v })}
          options={[
            { value: "", label: "All" },
            { value: "pending", label: "Waiting", count: count("pending") },
            { value: "scheduled", label: "Scheduled", count: count("scheduled") },
            { value: "running", label: "Deploying", count: count("running") },
            { value: "completed", label: "Completed", count: count("completed") },
            { value: "closed", label: "Closed", count: count("closed") },
          ]}
        />
        {this.user?.isAdmin ? (
          <ToggleButton
            type="button"
            active={Boolean(q.get("mine"))}
            aria-pressed={Boolean(q.get("mine"))}
            onClick={() => q.set({ mine: q.get("mine") ? null : "1" })}
            data-test-id="cutovers-waitingForMe"
            title="Requests someone else made that you can approve or reject"
          >
            <UserCheck size={14} />
            Waiting for me
            <span className="count">{waiting}</span>
          </ToggleButton>
        ) : null}
        <FilterSelect
          label="Transfer"
          value={q.get("transfer")}
          onChange={v => q.set({ transfer: v })}
          options={transferOptions}
          allLabel="Any"
        />
        <ToolbarDateRange label="Window" from={q.get("from")} to={q.get("to")} onChange={r => q.set({ from: r.from, to: r.to })} />
        {this.filtersActive ? <ClearFiltersButton onClick={() => this.clearFilters()} /> : null}
        <ToolbarSpacer />
        <ToolbarCount>{pluralise(records.length, "request")}</ToolbarCount>
        <RefreshButton onClick={() => this.load()} />
      </ListToolbar>
    );
  }

  renderApprovals(r: CutoverRequest) {
    const approvals = r.approvals || [];
    if (!approvals.length) {
      return r.status === "pending_approval" ? (
        <Pill tone="warning">Waiting</Pill>
      ) : (
        <Muted>—</Muted>
      );
    }
    return (
      <Decisions>
        {approvals.slice(0, 2).map(a => (
          <span className="d" key={a.id} title={a.comment || undefined} style={{ color: a.decision === "approved" ? "var(--t9-success-700)" : "var(--t9-danger-700)" }}>
            {a.decision === "approved" ? <Check size={12} /> : <X size={12} />}
            {a.user_name || a.user_id.slice(0, 8)}
          </span>
        ))}
        {approvals.length > 2 ? <Secondary>+{approvals.length - 2} more</Secondary> : null}
      </Decisions>
    );
  }

  renderRows(records: CutoverRequest[]) {
    if (this.state.loading && !this.state.loaded) {
      return (
        <Loading>
          <LoaderCircle className="spin" size={20} />
          Loading the cut-over requests…
        </Loading>
      );
    }
    if (!records.length) {
      return this.filtersActive ? (
        <EmptyState
          compact
          icon={SearchX}
          tone="neutral"
          title="No cut-over requests match these filters."
          message="Try a different search, status or window, or clear the filters."
          secondaryLabel="Clear filters"
          onSecondary={() => this.clearFilters()}
        />
      ) : (
        <EmptyState
          compact
          icon={ClipboardCheck}
          title="No cut-over requests yet"
          message="Request a cut-over from a transfer's page. Someone else approves it, and SwiftIQ deploys inside the change window."
          secondaryLabel="Open transfers"
          onSecondary={() => this.props.onNavigate("/transfers")}
        />
      );
    }
    const now = Date.now();
    return (
      <>
        <ListHeader columns={CUTOVER_COLUMNS} />
        {records.map(r => {
          const info = statusInfo(r.status);
          const start = parseApiDate(r.window_start);
          const end = parseApiDate(r.window_end);
          const active = ACTIVE_STATUSES.includes(r.status);
          const transfer = this.state.transfers[r.transfer_id];
          return (
            <ListRow
              key={r.id}
              columns={CUTOVER_COLUMNS}
              onClick={() => this.open(r)}
              selected={this.props.selectedId === r.id}
              actions={this.rowActions(r)}
              testId="cutoverRow"
            >
              <Stack>
                <Primary title={transfer?.name || r.transfer_id}>{transfer?.name || <Muted>{r.transfer_id.slice(0, 8)}</Muted>}</Primary>
                <Secondary title={r.reason || undefined}>
                  {instancesLabel(r, transfer?.instances)}
                  {r.reason ? ` · ${r.reason}` : ""}
                </Secondary>
              </Stack>
              <Stack title={formatWindow(r.window_start, r.window_end)}>
                <Primary style={{ fontSize: 13, fontWeight: 500 }}>{formatWhen(r.window_start)}</Primary>
                <Secondary>
                  {start && end ? `until ${endLabel(start, end)} · ${formatDuration(end.getTime() - start.getTime())}` : ""}
                  {active && start && start.getTime() > now ? ` · opens ${relative(r.window_start, now)}` : ""}
                  {active && start && end && start.getTime() <= now && end.getTime() > now ? " · open now" : ""}
                </Secondary>
              </Stack>
              <div>
                <Pill tone={info.tone} icon={info.icon} spin={info.spin} title={r.status_reason || undefined}>
                  {info.label}
                </Pill>
                {waitingForMe(r, this.user) ? (
                  <Secondary style={{ marginTop: 3, color: "var(--t9-warning-700)" }}>Needs your decision</Secondary>
                ) : null}
              </div>
              <Stack>
                <Primary style={{ fontSize: 13, fontWeight: 500 }}>{requesterName(r)}</Primary>
                <Secondary>{timeAgo(r.created_at)}</Secondary>
              </Stack>
              <div>{this.renderApprovals(r)}</div>
              <Cell>
                {r.deployment_id ? (
                  <span onClick={e => e.stopPropagation()}>
                    {this.state.deployments[r.deployment_id] ? (
                      <DeploymentLink
                        href={`/deployments/${r.deployment_id}`}
                        onClick={e => {
                          e.preventDefault();
                          this.props.onNavigate(`/deployments/${r.deployment_id}`);
                        }}
                      >
                        <StatePill status={this.state.deployments[r.deployment_id]} />
                      </DeploymentLink>
                    ) : (
                      <DeploymentLink
                        href={`/deployments/${r.deployment_id}`}
                        onClick={e => {
                          e.preventDefault();
                          this.props.onNavigate(`/deployments/${r.deployment_id}`);
                        }}
                      >
                        Open
                      </DeploymentLink>
                    )}
                  </span>
                ) : (
                  <Muted>—</Muted>
                )}
              </Cell>
              <Cell title={formatShortDateTime(r.created_at)}>{formatShortDateTime(r.created_at)}</Cell>
            </ListRow>
          );
        })}
      </>
    );
  }

  render() {
    const records = this.filtered();
    const selected = this.state.selected;
    return (
      <Wrapper>
        <MainTemplate
          navigationComponent={<Navigation currentPage="cutovers" />}
          listNoMargin
          headerComponent={
            <PageHeader
              title="Change control"
              subtitle="Cut-over requests and disaster recovery runs: someone other than the requester approves each one, and SwiftIQ deploys inside its change window."
              onProjectChange={() => this.load()}
            />
          }
          listComponent={
            <Card>
              {this.state.error ? (
                <div style={{ padding: "12px 16px 0 16px" }}>
                  <Notice tone="danger" title="SwiftIQ did not answer">
                    {this.state.error}
                  </Notice>
                </div>
              ) : null}
              <DrApprovals userId={this.user?.id} />
              {this.renderToolbar(records)}
              <Scroll>
                <Inner>{this.renderRows(records)}</Inner>
              </Scroll>
              <Footnote>
                Requests are records of changes: they are kept after they end and cannot be deleted. Times are in your time
                zone.
              </Footnote>
            </Card>
          }
        />
        {selected ? (
          <CutoverDetails
            request={selected}
            transferName={this.transferName(selected)}
            transferInstances={this.state.transfers[selected.transfer_id]?.instances}
            deploymentStatus={selected.deployment_id ? this.state.deployments[selected.deployment_id] : null}
            user={this.user}
            onClose={() => this.props.onNavigate(`/cutovers${window.location.search}`)}
            onChanged={r => this.replaceRequest(r)}
          />
        ) : null}
      </Wrapper>
    );
  }
}

function CutoversPage() {
  const query = useListQuery();
  const navigate = useNavigate();
  const { id } = useParams();
  return <CutoversPageContent query={query} selectedId={id || null} onNavigate={navigate} />;
}

export default CutoversPage;
